// React imports
import React, { useMemo } from 'react';

// Generic imports
import _ from 'lodash';

// Mui imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import { alpha, useTheme } from '@mui/material/styles';
import { green } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlane, faShip, faTruck } from '@fortawesome/pro-light-svg-icons';

// Local imports
import OpportunitiesFilterOwner from '../components/OpportunitiesFilter.owner.js';
import { PIPELINE_TRADELINES } from '../accounts/AccountFunnelPipeline.js';
import PIPELINE_COLORS from '../accounts/AccountFunnelPipeline.colors.js';

function TradelinesFilter(props) {
    const { filter, setFilter, org, me, tradelines, accounts } = props;

    const originOptions = useMemo(() =>
        _.sortBy(_.uniq((tradelines || []).flatMap(t => (t.routes || []).map(r => r.source?.text)).filter(Boolean))),
    [tradelines]);

    const destinationOptions = useMemo(() =>
        _.sortBy(_.uniq((tradelines || []).flatMap(t => (t.routes || []).map(r => r.destination?.text)).filter(Boolean))),
    [tradelines]);

    const searchOptions = useMemo(() => {
        const tags = _([...tradelines, ...(accounts || [])])
            .map(a => a.tags)
            .flatten()
            .uniqBy(a => a?.label)
            .compact()
            .sortBy(a => a?.label)
            .filter(a => !(filter.tags || []).find(t => _.isEqual(t,a)))
            .value();
        const _accounts = _(accounts)
            .map(a => a.name)
            .uniq()
            .sort()
            .filter(a => !(filter.accounts || []).find(t => t.label.toUpperCase() === a.toUpperCase()))
            .value();
        return [
            ...tags.map(t => ({ type: 'tag', value: t })), 
            ..._accounts.map(n => ({ type: 'name', value: n })),
        ]
    }, [accounts, filter]);

    const theme = useTheme();

    const pillSx = {
        fontSize: '11px', fontWeight: 500, textTransform: 'none',
        px: '10px', py: '3px', minWidth: 0, borderRadius: '14px',
        lineHeight: 1.5, whiteSpace: 'nowrap',
    };
    const pillInactive = {
        ...pillSx,
        backgroundColor: alpha(theme.palette.text.primary, 0.04),
        color: theme.palette.text.secondary,
        '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.08) },
    };
    const pillActive = {
        ...pillSx,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main, fontWeight: 600,
        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
    };

    return (
        <Box sx={{
            backgroundColor: '#F9FAFB',
            border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            borderRadius: '12px',
            p: '12px 16px',
            mb: 2,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <OpportunitiesFilterOwner org={org} filter={filter} setFilter={setFilter} me={me} noMarginRight={true} />

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                {/* Transport mode toggles */}
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    {[
                        { method: 'sea', icon: faShip, label: 'Sea' },
                        { method: 'air', icon: faPlane, label: 'Air' },
                        { method: 'land', icon: faTruck, label: 'Land' },
                    ].map(({ method, icon, label }) => {
                        const active = (filter.transportationMethods || []).includes(method);
                        return (
                            <Tooltip key={method} title={`Filter by ${label} tradelines`} arrow placement='bottom'>
                                <IconButton
                                    size='small'
                                    onClick={() => setFilter(f => ({
                                        ...f,
                                        transportationMethods: active
                                            ? (f.transportationMethods || []).filter(t => t !== method)
                                            : [...(f.transportationMethods || []), method]
                                    }))}
                                    sx={{
                                        fontSize: '13px', borderRadius: '6px', padding: '4px 8px',
                                        color: active ? '#1976d2' : '#94a3b8',
                                        backgroundColor: active ? alpha('#1976d2', 0.1) : 'transparent',
                                        '&:hover': { backgroundColor: active ? alpha('#1976d2', 0.18) : alpha('#000', 0.04) },
                                    }}
                                >
                                    <FontAwesomeIcon icon={icon} />
                                </IconButton>
                            </Tooltip>
                        );
                    })}
                </Box>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                {/* Status pills */}
                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'center', mr: '-2px' }}>Status</Typography>
                <Box sx={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                    <Button size='small' onClick={() => setFilter(f => ({ ...f, tradelineStatus: 'all' }))} sx={filter.tradelineStatus === 'all' ? pillActive : pillInactive}>All</Button>
                    <Button size='small' onClick={() => setFilter(f => ({ ...f, tradelineStatus: filter.tradelineStatus === 'running' ? 'all' : 'running' }))} sx={filter.tradelineStatus === 'running' ? pillActive : pillInactive}>Running</Button>
                    <Button size='small' onClick={() => setFilter(f => ({ ...f, tradelineStatus: filter.tradelineStatus === 'completed' ? 'all' : 'completed' }))}
                        sx={filter.tradelineStatus === 'completed' ? { ...pillSx, backgroundColor: alpha(green[600], 0.1), color: green[600], fontWeight: 600, '&:hover': { backgroundColor: alpha(green[600], 0.18) } } : pillInactive}>
                        Completed
                    </Button>
                    <Button size='small' onClick={() => setFilter(f => ({ ...f, tradelineStatus: filter.tradelineStatus === 'failed' ? 'all' : 'failed' }))}
                        sx={filter.tradelineStatus === 'failed' ? { ...pillSx, backgroundColor: alpha(theme.palette.error.main, 0.1), color: theme.palette.error.main, fontWeight: 600, '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.18) } } : pillInactive}>
                        Failed
                    </Button>
                </Box>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                {/* Phase (pipeline) filter */}
                <Typography sx={{ fontSize: '10px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', alignSelf: 'center', mr: '-2px' }}>Phase</Typography>
                <Select
                    size='small'
                    displayEmpty
                    value={filter.pipelinePhase || 'all'}
                    onChange={(e) => setFilter(f => ({ ...f, pipelinePhase: e.target.value === 'all' ? null : e.target.value }))}
                    sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 120, '& .MuiSelect-select': { py: '5px' } }}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value='all'>All Phases</MenuItem>
                    {PIPELINE_TRADELINES.map(phase => (
                        <MenuItem key={phase} value={phase}>{phase}</MenuItem>
                    ))}
                </Select>

                <Divider orientation='vertical' flexItem sx={{ mx: '4px', borderColor: alpha(theme.palette.text.primary, 0.1) }} />

                {/* Type filter */}
                <Select
                    size='small'
                    displayEmpty
                    value={filter.tradelineType}
                    onChange={(e) => setFilter({ ...filter, tradelineType: e.target.value })}
                    sx={{ fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff', minWidth: 100, '& .MuiSelect-select': { py: '5px' } }}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value='all'>All Types</MenuItem>
                    <MenuItem value='account'>Account</MenuItem>
                    <MenuItem value='salesperson'>Salesperson</MenuItem>
                </Select>
            </Box>

            {/* Origin / Destination filters */}
            <Box sx={{ display: 'flex', gap: 2, mt: '8px', flexWrap: 'wrap' }}>
                <Autocomplete
                    multiple freeSolo size='small'
                    options={originOptions}
                    value={filter.originFilter || []}
                    onChange={(e, v) => setFilter(f => ({ ...f, originFilter: v }))}
                    filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                    renderInput={(params) => <TextField {...params} label='Origin' placeholder='Type to search...' />}
                    sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                />
                <Autocomplete
                    multiple freeSolo size='small'
                    options={destinationOptions}
                    value={filter.destinationFilter || []}
                    onChange={(e, v) => setFilter(f => ({ ...f, destinationFilter: v }))}
                    filterOptions={(options, { inputValue }) => inputValue ? options.filter(o => o.toLowerCase().includes(inputValue.toLowerCase())) : options}
                    renderInput={(params) => <TextField {...params} label='Destination' placeholder='Type to search...' />}
                    sx={{ flex: 1, minWidth: '180px', '& .MuiInputBase-root': { fontSize: '12px', borderRadius: '8px', backgroundColor: '#fff' } }}
                />
            </Box>

            <Box sx={{ mb: 2, mt: 2 }}>
                <Autocomplete
                    options={searchOptions}
                    filterOptions={(options, state) => {
                        const { inputValue } = state;
                        const type = inputValue.startsWith('#') ? ['tag'] : inputValue.startsWith('@') ? ['name'] : null;
                        if (!type) return [];
                        const value = inputValue.substr(1);
                        if (value.length === 0) return [];
                        return options.filter(o => type.includes(o.type) && (o.value.label || o.value).toLowerCase().startsWith(value.toLowerCase()));
                    }}
                    getOptionLabel={option => {
                        if (typeof option === 'string') return option;
                        if (typeof option.value === 'string') return option.value;
                        if (typeof option.value?.label === 'string') return option.value.label;
                        return '';
                    }}
                    freeSolo
                    multiple
                    onChange={(e, value) => {
                        const tags = [
                            ...value.filter(v => v.type === 'tag').map(t => t.value),
                            ...value.filter(v => typeof (v) === 'string' && v.startsWith('#')).map(t => ({ label: t.substr(1) }))
                        ];
                        const _accounts = [
                            ...value.filter(v => v.type === 'name').map(t => t.value),
                            ...value.filter(v => typeof (v) === 'string' && v.startsWith('@')).map(t => t.substr(1))
                        ].map(ac => {
                            return ac;
                        }).map(ac => ({
                            label: ac,
                            ids: accounts.filter(a => (a?.name || '').toUpperCase() === (ac || '').toUpperCase()).map(a => a.id)
                        }));
                        const freetext = [
                            ...value.filter(v => v.type === 'freetext').map(t => t.value),
                            ...value.filter(v => typeof (v) === 'string' && !v.startsWith('#') && !v.startsWith('@')).map(t => t)
                        ];
                        setFilter(f => ({ ...f, tags, accounts: _accounts, freetext }));
                    }}
                    renderTags={(value, props) => {
                        return _.compact([
                            ...(filter.tags || []).map((t, i) => <Chip label={'Tag: ' + t.label} {...props({ index: i })} />),
                            ...(filter.accounts || []).map((n, i) => <Chip label={'Account: ' + n.label} {...props({ index: (filter.tags || []).length + i })} />),
                            ...filter.freetext.map((n, i) => <Chip label={'Free text: ' + n} {...props({ index: (filter.tags || []).length + (filter.accounts || []).length + i })} />)
                        ]);
                    }}
                    renderInput={(params) => <TextField placeholder='Search... (Use @ to search by account, # for hash, or just enter text for free text search)' {...params} />}
                    value={[
                        ...(filter.tags || []).map(t => ({ type: 'tag', value: t })),
                        ...(filter.accounts || []).map(n => ({ type: 'name', value: n.label })),
                        ...(filter.freetext || []).map(n => ({ type: 'freetext', value: n }))
                    ]}
                />

            </Box>
        </Box>
    );
}

function applyFilter({ filter, tradelines = [], org, accounts }) {
    return _.sortBy(tradelines.filter(tradeline => {
        const tags = [...(tradeline.tags || []), ...(accounts.find(a => a.id === tradeline.accountId)?.tags || [])];
        if (!_.isEmpty(filter.tags || []) && (_.isEmpty(_.intersectionWith(filter.tags || [], tags || [], _.isEqual)))) return false;
        if (filter.members && filter.members.length > 0) {
            if (!_.includes(filter.members, tradeline.owner)) return false;
        }
        if ((!_.isEmpty(filter.accounts)) && (!_.flatten(filter.accounts.map(a => a.ids)).includes(tradeline.accountId))) return false;
        const tradelineType = tradeline.accountId ? 'account' : 'salesperson';
        if ((filter.tradelineType !== 'all') && (tradelineType !== filter.tradelineType)) return false;
        if (filter.tradelineStatus === 'failed') {
            if (tradeline.status !== 'failed') return false;
        } else if (filter.tradelineStatus === 'completed') {
            if (tradeline.status !== 'completed') return false;
        } else if (filter.tradelineStatus === 'running') {
            if (tradeline.status === 'lost') return false;
            if (tradeline.status === 'completed') return false;
        }
        if (!_.isEmpty(filter.transportationMethods) && !filter.transportationMethods.includes(tradeline.transportationMethod)) return false;
        if (filter.pipelinePhase) {
            const pTag = (tradeline.tags || []).find(t => t.label?.startsWith('Pipeline: '));
            const phase = pTag ? pTag.label.replace('Pipeline: ', '') : 'New';
            if (phase !== filter.pipelinePhase) return false;
        }
        if ((filter.originFilter || []).length > 0) {
            const origins = (tradeline.routes || []).map(r => r.source?.text || 'Everywhere');
            if (!filter.originFilter.some(f => origins.some(o => o.toLowerCase().includes(f.toLowerCase())))) return false;
        }
        if ((filter.destinationFilter || []).length > 0) {
            const destinations = (tradeline.routes || []).map(r => r.destination?.text || 'Everywhere');
            if (!filter.destinationFilter.some(f => destinations.some(d => d.toLowerCase().includes(f.toLowerCase())))) return false;
        }
        if (_.some(filter.freetext, freetext => {
            return (JSON.stringify(tradeline).toLowerCase().indexOf(freetext.toLowerCase()) === -1)
                && (JSON.stringify(accounts.find(ac => ac.id === tradeline.accountId)).toLowerCase().indexOf(freetext.toLowerCase()) === -1)
        })) {
            return false;
        }
        return true;
    }), tradeline => -1 * tradeline.created);
}

export default TradelinesFilter;
export { applyFilter };
